import { create } from 'zustand';
import db from '../../db';
import { httpService } from '../utils/apiService';

const ADMIN_ROLES = ['SUPER_ADMIN', 'ADMIN', 'READ_ONLY_ADMIN'];
const SCOPED_ROLES = ['OPERATOR', 'LOCATION_OWNER'];

export const useOperatorStore = create((set, get) => ({
  operatorId: null,
  name: null,
  /** false until default/session operator is bound after auth */
  isOperatorReady: false,
  operatorBindError: null,

  setOperatorID: async (id, name) => {
    const normalizedId = id != null ? String(id) : null;
    if (normalizedId) {
      await db.operator.put({ key: 'operatorId', value: normalizedId });
      await db.operator.put({ key: 'name', value: name ?? null });
    } else {
      await db.operator.clear();
    }
    set({
      operatorId: normalizedId,
      name: name ?? null,
      isOperatorReady: !!normalizedId,
      operatorBindError: normalizedId ? null : get().operatorBindError,
    });
  },

  clearOperatorID: async () => {
    await db.operator.clear();
    set({
      operatorId: null,
      name: null,
      isOperatorReady: false,
      operatorBindError: null,
    });
  },

  /**
   * Bind operator for the current user.
   * Admins: always fetch GET /operator/default (ignores IndexedDB).
   * OPERATOR / LOCATION_OWNER: use session user.operatorId.
   */
  bindOperatorForUser: async (user) => {
    if (!user) {
      set({
        operatorId: null,
        name: null,
        isOperatorReady: false,
        operatorBindError: null,
      });
      return;
    }

    if (SCOPED_ROLES.includes(user.role) && user.operatorId) {
      await get().setOperatorID(user.operatorId, user.operatorName || null);
      return;
    }

    if (ADMIN_ROLES.includes(user.role)) {
      try {
        // Clear stale IndexedDB selection first
        await db.operator.clear();
        const response = await httpService.get('operator/default');
        const op = response?.data?.data;
        if (!op?._id) {
          throw new Error(
            'Default operator not found. Run seed:default-operator and set DEFAULT_OPERATOR_ID.',
          );
        }
        await get().setOperatorID(op._id, op.name);
      } catch (e) {
        const message =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load default operator';
        console.error('[operator] bind failed:', message);
        set({
          operatorId: null,
          name: null,
          isOperatorReady: false,
          operatorBindError: message,
        });
      }
      return;
    }

    // Unknown role — mark ready without operator
    set({ isOperatorReady: true, operatorBindError: null });
  },
}));
